import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { X, Flask, ArrowRight } from "@phosphor-icons/react";

const ACTIONS = [
  ["", "No change"], ["remove", "Remove step"], ["automate", "Automate"],
  ["ai_agent", "AI agent"], ["ai_augment", "AI augment"], ["simplify", "Simplify"],
];
const ACTION_COLORS = { remove: "#EF4444", automate: "#3B82F6", ai_agent: "#6366F1", ai_augment: "#A855F7", simplify: "#F97316" };
const CLS_TO_ACTION = { eliminate: "remove", automate: "automate", ai_agent: "ai_agent", ai_augment: "ai_augment", simplify: "simplify" };

export function SimulationPanel({ wf, depts, onClose }) {
  const [interventions, setInterventions] = useState({});
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const deptByKey = Object.fromEntries(depts.map((d) => [d.key, d]));

  const setAction = (stepId, action) => {
    const next = { ...interventions };
    if (action) next[stepId] = action; else delete next[stepId];
    setInterventions(next);
    setResult(null);
  };

  const useClassifications = () => {
    const next = {};
    wf.steps.forEach((s) => { const a = CLS_TO_ACTION[s.classification]; if (a) next[s.id] = a; });
    setInterventions(next);
    setResult(null);
    toast.info("Applied each step's work-redesign classification as its intervention");
  };

  const run = async () => {
    if (Object.keys(interventions).length === 0) { toast.error("Select at least one intervention first"); return; }
    setRunning(true);
    try {
      const { data } = await api.post(`/workflows/${wf.id}/simulate`, { interventions });
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setRunning(false);
  };

  const metric = (label, base, sim, unit, invert = true) => {
    const delta = sim - base;
    const good = invert ? delta < 0 : delta > 0;
    return (
      <div className="bg-white/8 border border-white/12 rounded-sm p-3" data-testid={`sim-metric-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
        <p className="text-[10px] uppercase tracking-[0.15em] text-white/55">{label}</p>
        <div className="flex items-baseline gap-2 mt-1">
          <span className="text-white/60 text-sm">{base}{unit}</span>
          <ArrowRight size={12} className="text-white/40" />
          <span className="font-display text-2xl font-black" style={{ color: delta === 0 ? "#FDFBF7" : good ? "#22C55E" : "#EF4444" }}>{sim}{unit}</span>
        </div>
        <p className="text-[11px] mt-0.5" style={{ color: delta === 0 ? "#ffffff66" : good ? "#22C55E" : "#EF4444" }}>
          {delta > 0 ? "+" : ""}{Math.round(delta * 10) / 10}{unit}
        </p>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-[#0A0A0A] text-white rounded-sm w-full max-w-5xl max-h-[90vh] overflow-y-auto fade-up" onClick={(e) => e.stopPropagation()} data-testid="simulation-panel">
        <div className="sticky top-0 bg-[#0A0A0A] border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Flask size={18} weight="duotone" className="text-[#D4AF37]" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">What-if simulation</p>
              <h3 className="font-display font-bold text-lg tracking-tight">{wf.name}</h3>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={useClassifications} data-testid="sim-use-classifications-btn"
              className="text-[11px] border border-[#D4AF37]/50 text-[#D4AF37] rounded-sm px-3 py-1.5 hover:bg-[#D4AF37]/10 transition-colors">Use step classifications</button>
            <button onClick={() => { setInterventions({}); setResult(null); }} data-testid="sim-clear-btn"
              className="text-[11px] border border-white/20 text-white/70 rounded-sm px-3 py-1.5 hover:border-white/50 transition-colors">Clear</button>
            <button onClick={run} disabled={running} data-testid="sim-run-btn"
              className="text-[12px] bg-[#D4AF37] text-[#0A0A0A] rounded-sm px-4 py-1.5 font-semibold hover:bg-[#e0c258] transition-colors disabled:opacity-50">
              {running ? "Simulating…" : "Run simulation"}</button>
            <button onClick={onClose} data-testid="sim-close-btn" className="ml-2 text-white/60 hover:text-white transition-colors"><X size={18} /></button>
          </div>
        </div>

        {result && (
          <div className="px-6 py-4 border-b border-white/10 fade-up" data-testid="sim-results">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {metric("Cycle time", result.baseline.cycle_days, result.simulated.cycle_days, "d")}
              {metric("Human effort", result.baseline.effort_hours, result.simulated.effort_hours, "h")}
              {metric("Wait time", result.baseline.wait_hours, result.simulated.wait_hours, "h")}
              {metric("Hand-offs", result.baseline.handoffs, result.simulated.handoffs, "")}
            </div>
            <div className="flex flex-wrap gap-4 mt-3 text-[12px]">
              <span className="text-[#22C55E] font-semibold" data-testid="sim-cycle-reduction">Cycle time reduced {result.cycle_reduction_pct}%</span>
              <span className="text-white/70">Capacity released: {result.capacity_released_hours}h per instance</span>
              <span className="text-white/70">Issue flags resolved: {result.flags_resolved}</span>
            </div>
            {result.notes.map((n, i) => <p key={i} className="text-[11px] text-amber-300/90 mt-2">⚠ {n}</p>)}
          </div>
        )}

        <div className="px-6 py-4">
          <p className="text-[10px] uppercase tracking-[0.15em] text-white/50 mb-2">Assign interventions per step</p>
          <div className="space-y-1.5">
            {wf.steps.map((s) => {
              const dept = deptByKey[s.department_key];
              const sel = interventions[s.id] || "";
              const sim = result?.per_step.find((p) => p.id === s.id);
              return (
                <div key={s.id} className={`flex items-center gap-3 border border-white/10 rounded-sm px-3 py-2 ${sim?.removed ? "opacity-40" : ""}`} data-testid={`sim-step-${s.order}`}>
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: dept?.color }} title={dept?.name} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] truncate">{s.order + 1}. {s.name} <span className="text-white/40">· {s.role}</span></p>
                    <p className="text-[10px] text-white/40">{s.effort_mins}m effort · {s.wait_mins}m wait{s.flags.length > 0 && ` · ${s.flags.length} flag(s)`}</p>
                  </div>
                  {sim && (sim.after_effort !== sim.before_effort || sim.after_wait !== sim.before_wait) && (
                    <span className="text-[10px] text-[#22C55E] shrink-0" data-testid={`sim-step-delta-${s.order}`}>
                      {sim.removed ? "removed" : `→ ${sim.after_effort}m / ${sim.after_wait}m`}
                    </span>
                  )}
                  <select value={sel} onChange={(e) => setAction(s.id, e.target.value)} data-testid={`sim-action-select-${s.order}`}
                    className="text-[11px] rounded-sm px-2 py-1.5 bg-white/10 border border-white/15 shrink-0"
                    style={sel ? { background: ACTION_COLORS[sel], color: "#fff", borderColor: ACTION_COLORS[sel] } : {}}>
                    {ACTIONS.map(([v, l]) => <option key={v} value={v} className="text-black bg-white">{l}</option>)}
                  </select>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
